import type { Theme } from '@mui/material/styles';
import type { CSSObject } from 'tss-react';

import { DRAFT_PREVIEW_DATA_ATTRIBUTES } from './sanitize-html';
import type { DraftPreviewDataAttribute } from './sanitize-html';

function previewDataAttributeSelector(attribute: DraftPreviewDataAttribute, value = 'true') {
  return `& [${attribute}="${value}"]`;
}

/**
 * Shared CSS for list-item previews emitted by
 * `renderAlineDraftListMarkersForPreview` — the marker/content grid and
 * per-level indentation. Used by both the drafter card and the redline insert
 * card so their list rendering stays in sync.
 *
 * `contentBackground` is for the insert/remove cards: it tints the suggested
 * text (the content span) rather than the whole grid row, and neutralizes the
 * card's blanket `& p` tint/width on the list-item row so the grid lays out
 * normally.
 *
 * `markerColumnWidth` sizes the marker column — and thus the first-level indent,
 * since level 0 has no `marginLeft`. The drafter card uses a fixed `3rem` so
 * markers right-align in a tidy column; the narrow insert/remove cards pass
 * `auto` so the marker hugs the left edge instead of wasting space.
 */
export function previewListStyles(
  theme: Theme,
  options: { contentBackground?: string; markerColumnWidth?: string } = {}
): Record<string, CSSObject> {
  const { contentBackground, markerColumnWidth = '3rem' } = options;
  return {
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listContent)]: {
      minWidth: 0,
      ...(contentBackground ? { backgroundColor: contentBackground, width: 'fit-content' } : {}),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listItem)]: {
      alignItems: 'baseline',
      columnGap: theme.spacing(1),
      display: 'grid',
      gridTemplateColumns: `${markerColumnWidth} minmax(0, 1fr)`,
      ...(contentBackground ? { backgroundColor: 'transparent', margin: 0, width: 'auto' } : {}),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '1')]: {
      marginLeft: theme.spacing(3),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '2')]: {
      marginLeft: theme.spacing(6),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '3')]: {
      marginLeft: theme.spacing(9),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '4')]: {
      marginLeft: theme.spacing(12),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '5')]: {
      marginLeft: theme.spacing(15),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '6')]: {
      marginLeft: theme.spacing(18),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '7')]: {
      marginLeft: theme.spacing(21),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listLevel, '8')]: {
      marginLeft: theme.spacing(24),
    },
    [previewDataAttributeSelector(DRAFT_PREVIEW_DATA_ATTRIBUTES.listMarker)]: {
      color: 'inherit',
      font: 'inherit',
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
  };
}
